import { useState, useEffect } from "react";
import gsap from "gsap";
import SplitType from "split-type";
import { Link } from "react-router-dom";
import "./Welcome.css";

const API = process.env.REACT_APP_API_URL || "";

const IMAGE_CARDS = [
    {
        key: "welcome_hero1",
        fallback: "https://images.unsplash.com/photo-1508614589041-895b88991e3e?w=800&q=85",
        tag: "Agri Simulation",
        title: "Built To Fly",
        path: "/businesses/agri-sim",
    },
    {
        key: "welcome_hero2",
        fallback: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=800&q=85",
        tag: "Agriculture",
        title: "Precision Spraying",
        path: "/our-businesses/agri",
    },
    {
        key: "welcome_hero3",
        fallback: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=85",
        tag: "Survey & Mapping",
        title: "Eyes From Above",
        path: "/our-businesses/camera",
    },
    {
        key: "welcome_hero4",
        fallback: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=85",
        tag: "Enterprise Ops",
        title: "Mission Ready",
        path: "/our-businesses/overview",
    },
];

/* Aerospace targeting reticle — decorative, zero layout impact */
function Reticle() {
    return (
        <svg
            className="wlc-reticle"
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            {/* Outer ring */}
            <circle cx="100" cy="100" r="94" stroke="currentColor" strokeWidth="0.6" />

            {/* Tick marks every 30° on outer ring */}
            {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i * 30 * Math.PI) / 180;
                const outer = 94;
                const inner = i % 3 === 0 ? 84 : 89;
                const x1 = 100 + outer * Math.cos(angle);
                const y1 = 100 + outer * Math.sin(angle);
                const x2 = 100 + inner * Math.cos(angle);
                const y2 = 100 + inner * Math.sin(angle);
                return (
                    <line
                        key={i}
                        x1={x1} y1={y1}
                        x2={x2} y2={y2}
                        stroke="currentColor"
                        strokeWidth={i % 3 === 0 ? "1" : "0.5"}
                    />
                );
            })}

            {/* Middle ring — counter-rotates via CSS */}
            <g className="wlc-reticle-inner">
                <circle cx="100" cy="100" r="58" stroke="currentColor" strokeWidth="0.5" strokeDasharray="4 6" />
            </g>

            {/* Inner solid ring */}
            <circle cx="100" cy="100" r="26" stroke="currentColor" strokeWidth="0.8" />

            {/* Crosshair lines — gaps at center */}
            <line x1="6" y1="100" x2="70" y2="100" stroke="currentColor" strokeWidth="0.6" />
            <line x1="130" y1="100" x2="194" y2="100" stroke="currentColor" strokeWidth="0.6" />
            <line x1="100" y1="6" x2="100" y2="70" stroke="currentColor" strokeWidth="0.6" />
            <line x1="100" y1="130" x2="100" y2="194" stroke="currentColor" strokeWidth="0.6" />

            {/* Corner brackets at 45° positions */}
            {[45, 135, 225, 315].map((deg) => {
                const rad = (deg * Math.PI) / 180;
                const r = 58;
                const cx = 100 + r * Math.cos(rad);
                const cy = 100 + r * Math.sin(rad);
                return (
                    <circle key={deg} cx={cx} cy={cy} r="2"
                        fill="currentColor" opacity="0.6" />
                );
            })}

            {/* Center dot */}
            <circle cx="100" cy="100" r="3.5" fill="currentColor" />
            <circle cx="100" cy="100" r="7" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
        </svg>
    );
}

export default function Welcome() {
    const [images, setImages] = useState({});

    useEffect(() => {
        fetch(`${API}/api/media/welcome`)
            .then((res) => (res.ok ? res.json() : {}))
            .then((data) => setImages(data))
            .catch(() => { });

        // 🔥 TEXT ANIMATION
        const split = new SplitType(".wlc-headline", {
            types: "chars"
        });

        gsap.from(split.chars, {
            y: 120,
            opacity: 0,
            stagger: 0.03,
            duration: 1,
            ease: "power4.out",
            delay: 0.2
        });

    }, []);

    return (
        <section className="wlc-wrap">
            <div className="wlc-grid">

                {/* ── HERO CARD ── */}
                <div className="wlc-hero">

                    {/* background layers — no layout impact */}
                    <div className="wlc-hero-pattern" aria-hidden="true" />
                    <Reticle />

                    {/* badge — only item in top section */}
                    <div className="wlc-hero-top">
                        <span className="wlc-badge">
                            <span className="wlc-badge-dot" />
                            Indigenous UAV Manufacturing
                        </span>
                    </div>

                    {/* main content — pinned to bottom */}
                    <div className="wlc-hero-body">
                        <p className="wlc-eyebrow">Who We Are</p>
                        <h1 className="wlc-headline">
                            Ground-To-<em>Sky</em>
                            <br />Drone
                            <br />Engineering.
                        </h1>
                        <p className="wlc-sub">
                            Aviatricks delivers end-to-end UAV solutions — aeronautical design,
                            embedded hardware, flight software, and field deployment under one roof.
                        </p>
                        <div className="wlc-ctas">
                            <Link to="/our-businesses/overview" className="wlc-btn-primary">
                                Explore
                            </Link>
                            <Link to="/about" className="wlc-btn-secondary">
                                Our Story
                            </Link>
                        </div>
                    </div>

                </div>

                {/* ── IMAGE CARDS ── */}
                {IMAGE_CARDS.map((card, i) => (
                    <Link
                        key={card.key}
                        to={card.path}
                        className="wlc-img-card"
                        style={{ animationDelay: `${0.08 + i * 0.07}s` }}
                    >
                        <img
                            src={images[card.key] || card.fallback}
                            alt={card.title}
                            loading={i === 0 ? "eager" : "lazy"}
                        />
                        <div className="wlc-img-overlay">
                            <div className="wlc-img-meta">
                                <span className="wlc-img-tag">{card.tag}</span>
                                <span className="wlc-img-title">{card.title}</span>
                            </div>
                            <span className="wlc-img-arrow">↗</span>
                        </div>
                    </Link>
                ))}

            </div>
        </section>
    );
}